import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Spin, Alert } from 'antd';
import PropTypes from 'prop-types';
import paypal from 'paypal-checkout';
import client from 'braintree-web/client';
import paypalCheckout from 'braintree-web/paypal-checkout';
import { get } from '../../../utility/via-fetch';
import { uniqueId } from '../../../utility/via-util';
import { tokenPackageShape } from './purchase-util';


function Loading() {
  return (
    <div
      style={{
        flex: '0 1 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        color: 'rgb(33, 33, 33, 1)',
        fontStyle: 'italic',
      }}
    >
      <FormattedMessage id="connectingToPaymentProvider" />
      <Spin />
    </div>
  );
}


/**
 * A wrapper around the Braintree drop-in component. (Version 3)
 * @param {object} props React props
 * @param {string} props.environment The paypal environment. One of: ["sandbox", "production"]
 * @param {object} props.tokenPackage The token package being purchased.PropTypes
 * @param {func} props.onPayPalPayment A function to be executed on payment.
 * @param {func} props.onPayPalCancel A function to be executed on cancel.
 * @param {func} props.render A react component to render. (isLoading, getPaymentMethod) => ReactComponent
 * @param {object} props.style The js styles to apply to the wrapper container.
 * @param {string} props.className The className to apply to the wrapper container.
 */
export default function BrainTreeDropIn({
  environment, tokenPackage, onPayPalPayment, onPayPalCancel, render, style, className,
}) {
  const [id] = useState(uniqueId('paypal-drop-in-'));
  const [initError, setInitError] = useState();
  const containerRef = useRef();
  const [token, setToken] = useState();
  const [payPalButton, setPayPalButton] = useState();

  // Effect to load the token if its empty.
  useEffect(() => {
    if (token) return;

    (async () => {
      try {
        const loadedToken = await get('/api/tokens/purchase/braintree-client-token');
        setToken(loadedToken);
      } catch (e) {
        console.log(e); // eslint-disable-line no-console
        setInitError(e);
      }
    })();
  }, [setToken, token]);

  // Effect to initialise the payment container once we have our token.
  useEffect(() => {
    if (!token || !tokenPackage) return;

    containerRef.current.innerHTML = null;

    // Prepare the configuration of the drop-in.
    const dropInOptions = {
      authorization: token,
      container: '#dropin-container',
      paypal: {
        flow: 'checkout',
        amount: tokenPackage.price,
        currency: tokenPackage.currencyCode,
      },
    };

    (async () => {
      try {
        const clientInstance = await client.create(dropInOptions);
        const paypalCheckoutInstance = await paypalCheckout.create({ client: clientInstance });

        const button = await paypal.Button.render({
          env: environment,
          commit: true,
          style: {
            shape: 'rect',
            tagline: 'false',
            label: 'pay',
            size: 'responsive',
          },
          payment: () => paypalCheckoutInstance.createPayment({
            flow: 'checkout',
            amount: tokenPackage.price,
            currency: tokenPackage.currencyCode,
          }),
          onAuthorize: async (data) => {
            const payload = await paypalCheckoutInstance.tokenizePayment(data);
            // Note: The PayPal background dissappears after this function completes.
            //       So we use await to make sure any server calls are done while the background is still up.
            await onPayPalPayment(payload.nonce);
          },
          onCancel: () => { onPayPalCancel(); },
          onError: (err) => { console.warn('via', err); }, // eslint-disable-line no-console
        }, `#${id}`);
        setPayPalButton(button);
      } catch (e) {
        console.warn('Failed to initialise the PayPal button.', e); // eslint-disable-line no-console
        setInitError(e);
      }
    })();
  }, [environment, token, tokenPackage, id, setPayPalButton, onPayPalPayment, onPayPalCancel]);

  const isLoading = !payPalButton && !initError;

  return (
    <>
      <div style={style} className={className}>
        { isLoading && <Loading /> }
        { initError && <Alert message={<FormattedMessage id="paymentConnectionFail" />} type="error" showIcon />}
        <div ref={containerRef} id={id} style={{ display: isLoading || initError ? 'none' : undefined }} />
        {render && render()}
      </div>
    </>
  );
}

BrainTreeDropIn.propTypes = {
  environment: PropTypes.oneOf(['sandbox', 'production']).isRequired,
  tokenPackage: tokenPackageShape.isRequired,
  onPayPalPayment: PropTypes.func,
  onPayPalCancel: PropTypes.func,
  render: PropTypes.func,
  style: PropTypes.shape({}),
  className: PropTypes.string,
};

BrainTreeDropIn.defaultProps = {
  onPayPalPayment: () => {},
  onPayPalCancel: () => {},
  render: () => <></>,
  style: {},
  className: '',
};
